// System 6 — dangerous robots.
//
// The brief: "Larger enforcement machines, and a real threat. They have legible awareness states
// and they telegraph clearly before they engage. The player can be caught. Being caught should feel
// like being spotted by something much heavier than you, not like losing hit points."
//
// Reuses System 5's foundation exactly as the brief planned: Leg + Gait unchanged, bigger numbers.
// What is new here is the AWARENESS machine and the WEIGHT cues:
//
//   PATROL -> SCAN -> TELEGRAPH -> ENGAGE -> (CATCH | LOST -> SEARCH) -> PATROL
//
// Every transition emits a named event, because the brief's gate for this system is "the behaviour
// actually fires when the player does the thing that should trigger it" -- and a behaviour that
// cannot be observed from outside cannot be gated.
//
// Weight is carried by rates, not by size alone: acceleration ramps over ~0.8 s instead of stepping,
// the body turns at a limited rate, the stride is long and the duty factor high. A big model that
// accelerates instantly reads as a balloon.

import * as THREE from 'three';
import { Leg, Gait, part } from './creatures.js';
import { quadBody, quadHead, quadLimb, dressBone, cloneParts, cached } from './geometry/rigs.js';
import { strut } from './geometry/procedural.js';

const NOTICE_DIST = 14;         // scan trigger range
const CONE_COS = 0.62;          // ~cos(52 deg) half-angle -- it has eyes, not radar
// USER DECISION (third brief on this): NO quadruped ever resets the player. The old CATCH
// (fade-to-white + respawn) is deleted game-wide; reaching the player now produces CONFRONT --
// the machine plants itself at a 1.8 m stand-off, holds you in the red lamp, and stands ground as
// a physical barrier until you leave. The threat is the cornering, not a screen reset.
const STANDOFF = 1.8;
const LOSE_DIST = 26;
const ENGAGE_SPEED = 2.3;       // vs player walk 1.55 / run 3.4: catchable walking, escapable running
const PATROL_SPEED = 0.8;

export class Sentinel {
  constructor(scene, world, key) {
    this.world = world;
    this.key = key;

    this.root = new THREE.Group();
    this.body = new THREE.Group();
    this.root.add(this.body);
    scene.add(this.root);

    const shell = new THREE.MeshStandardMaterial({ color: 0x2e3238, roughness: 0.52, metalness: 0.78 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x121418, roughness: 0.6, metalness: 0.6 });
    this.lampMat = new THREE.MeshStandardMaterial({
      color: 0x0a0c10, emissive: 0xffc37a, emissiveIntensity: 0.8, roughness: 0.3,
    });

    // --- legs: same Leg/Gait as the fauna, scaled up ------------------------
    const UP = 1.05, LO = 1.15, HW = 0.55, HL = 0.75;
    // Lateral-sequence walk: each foot lifts alone, three always planted. Diagonal-pair trot
    // phases on a machine this size read as a scaled-up dog; LS walk is how heavy walkers move.
    const legSpecs = [
      { hip: [-HW, 0, HL], upper: UP, lower: LO, phase: 0.00, knee: -1 },
      { hip: [HW, 0, HL], upper: UP, lower: LO, phase: 0.50, knee: -1 },
      { hip: [-HW, 0, -HL], upper: UP, lower: LO, phase: 0.75, knee: 1 },
      { hip: [HW, 0, -HL], upper: UP, lower: LO, phase: 0.25, knee: 1 },
    ];
    this.legs = legSpecs.map((L) => new Leg(this.body, L));
    // duty 0.62: a heavy walker keeps more feet down for longer. stride locks the rate (S5 lesson).
    this.gait = new Gait({ duty: 0.75, stride: 1.05, lift: 0.17 });
    this.ride = (UP + LO) * 0.75;

    // --- hull (overhaul 3B: same chassis kit as the fauna, scaled 3.2x) ------
    const S = 3.2;
    dressBone(this.body, cloneParts(cached('sent.body', () =>
      quadBody('sentinel', S, { w: 0.31, h: 0.19, l: 0.59, hips: legSpecs.map((L) => [L.hip[0] / S, 0, L.hip[2]]) }))), 'mech');
    const limb = cached('sent.limb', () => quadLimb(UP, LO, 2.6));
    for (const L of legSpecs) {
      const leg = this.legs[legSpecs.indexOf(L)];
      dressBone(leg.thigh, cloneParts(limb.thigh), 'mech');
      dressBone(leg.shin, cloneParts(limb.shin), 'mech');
      dressBone(leg.foot, cloneParts(limb.foot), 'mech');
    }
    void shell; void dark;

    // --- sensor mast + head --------------------------------------------------
    this.mast = new THREE.Group();
    this.mast.position.set(0, 0.52, 0.7);
    this.body.add(this.mast);
    // 3B critic: "the sensor head hovers with no readable neck member". The mast now rakes
    // forward to meet the head base and carries a rear diagonal down to the shoulder line.
    dressBone(this.mast, cloneParts(cached('sent.mast', () => [
      ...strut([0, -0.04, -0.16], [0, 0.52, 0.04], 0.11, 0.085, { slot: 'mechDark', seg: 9 }),
      ...strut([0, 0.50, -0.02], [0, 0.02, -0.42], 0.045, 0.038, { slot: 'trim', seg: 7 }),
    ])), 'mech');
    this.head = new THREE.Group();
    this.head.position.set(0, 0.55, 0);
    this.mast.add(this.head);
    dressBone(this.head, cloneParts(cached('sent.head', () => quadHead('sentinel', 2.1))), 'mech');
    this.lamp = part(this.head, this.lampMat, 0.40, 0.09, 0.03, 0, 0.02, 0.242);
    // Corner marker strips share lampMat, so the whole outline goes red when it hunts.
    for (const [mx, mz] of [[-0.5, 0.92], [0.5, 0.92], [-0.5, -0.92], [0.5, -0.92]]) {
      part(this.body, this.lampMat, 0.05, 0.3, 0.05, mx, 0.28, mz);
    }

    // Searchlight: an actual light attached to the head, so the beam and the machine that carries
    // it are one object -- the System 3 rule applied to a moving fitting.
    this.beam = new THREE.SpotLight(0xffc37a, 30, 30, 0.42, 0.45, 1.6);
    this.beam.position.set(0, -0.02, 0.28);
    this.beamTarget = new THREE.Object3D();
    this.head.add(this.beam);
    this.head.add(this.beamTarget);
    this.beamTarget.position.set(0, -0.4, 6);
    this.beam.target = this.beamTarget;

    // --- route + state -------------------------------------------------------
    this.route = [[-2.2, -28], [-2.2, 24], [2.2, 24], [2.2, -28]];
    this.seg = 0; this.u = 0;
    this.pos = new THREE.Vector3(this.route[0][0], 0, this.route[0][1]);
    this.heading = 0;
    this.speed = 0;
    this.state = 'patrol';
    this.stateT = 0;
    this.losGone = 0;
    this.lastSeen = new THREE.Vector3();
    this.calm = false;             // gait-review pass: locomotion only, reactions off
    this.clearX = 0; this.clearZ = 0;
    this._inv = new THREE.Matrix4();

    // Plant feet before the first solve.
    for (const L of this.legs) {
      const s = Math.sin(this.heading), c = Math.cos(this.heading);
      const hx = this.pos.x + L.spec.hip[0] * c + L.spec.hip[2] * s;
      const hz = this.pos.z - L.spec.hip[0] * s + L.spec.hip[2] * c;
      L.planted.set(hx, world.groundAt(hx, hz, 0), hz);
      L.target.copy(L.planted);
    }
  }

  _emit(ev, name) { ev[name] = (ev[name] || 0) + 1; }

  _turnToward(targetHeading, dt, rate = 1.4) {
    let d = targetHeading - this.heading;
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    const step = Math.max(-rate * dt, Math.min(rate * dt, d));
    this.heading += step;
    return Math.abs(d) < 0.1;
  }

  _los(playerPos) {
    return this.world.lineOfSight(
      this.pos.x, this.pos.y + this.ride + 0.9, this.pos.z,
      playerPos.x, (playerPos.y || 0) + 1.5, playerPos.z);
  }

  _sees(playerPos, dist) {
    if (dist > NOTICE_DIST) return false;
    const fx = Math.sin(this.heading), fz = Math.cos(this.heading);
    const dx = (playerPos.x - this.pos.x) / dist, dz = (playerPos.z - this.pos.z) / dist;
    if (dx * fx + dz * fz < CONE_COS) return false;
    return this._los(playerPos);
  }

  update(dt, playerPos) {
    const ev = {};
    const dx = playerPos.x - this.pos.x, dz = playerPos.z - this.pos.z;
    const dist = Math.hypot(dx, dz);
    const bearing = Math.atan2(dx, dz);
    this.stateT += dt;

    // --- awareness ---------------------------------------------------------
    let wantSpeed = PATROL_SPEED;
    let headLook = null;               // world point the head tracks

    if (this.calm) {
      // Gait review: pure locomotion.
    } else if (this.state === 'patrol') {
      if (this._sees(playerPos, dist)) {
        this.state = 'scan'; this.stateT = 0; this._emit(ev, 'robot.scan');
      }
    } else if (this.state === 'scan') {
      wantSpeed = PATROL_SPEED * 0.15;
      headLook = playerPos;
      if (!this._sees(playerPos, dist) && this.stateT > 1.4) {
        this.state = 'patrol'; this._emit(ev, 'robot.resume');
      } else if (this.stateT > 1.1 && dist < NOTICE_DIST) {
        this.state = 'telegraph'; this.stateT = 0; this._emit(ev, 'robot.telegraph');
      }
    } else if (this.state === 'telegraph') {
      // The warning. Stationary, squared to the player, lamp hammering red. A full second --
      // legible, escapable, unmistakable.
      wantSpeed = 0;
      headLook = playerPos;
      this._turnToward(bearing, dt, 2.2);
      if (this.stateT > 1.0) {
        this.state = 'engage'; this.stateT = 0; this._emit(ev, 'robot.engage');
      }
    } else if (this.state === 'engage') {
      headLook = playerPos;
      this._turnToward(bearing, dt, 2.6);
      wantSpeed = ENGAGE_SPEED;
      if (this._los(playerPos)) { this.losGone = 0; this.lastSeen.set(playerPos.x, 0, playerPos.z); }
      else this.losGone += dt;
      if (dist < STANDOFF) {
        this.state = 'confront'; this.stateT = 0; this._emit(ev, 'robot.confront');
      } else if (this.losGone > 2.5 || dist > LOSE_DIST) {
        this.state = 'search'; this.stateT = 0; this._emit(ev, 'robot.lost');
      }
    } else if (this.state === 'confront') {
      // Stand ground: square to the player, hold the stand-off, zero damage, zero reset.
      wantSpeed = 0;
      headLook = playerPos;
      this._turnToward(bearing, dt, 2.0);
      if (dist > 3.2) { this.state = 'resume'; this.stateT = 0; this._emit(ev, 'robot.resume'); }
      else if (this.stateT > 10) { this.state = 'patrol'; this.stateT = 0; this._emit(ev, 'robot.resume'); }
    } else if (this.state === 'resume') {
      wantSpeed = PATROL_SPEED * 0.5;
      if (this.stateT > 1.2) this.state = 'patrol';
    } else if (this.state === 'search') {
      // Go to where it last saw you and sweep. Escape = stay out of the cone until it gives up.
      wantSpeed = PATROL_SPEED * 0.9;
      if (this._sees(playerPos, dist)) {
        this.state = 'telegraph'; this.stateT = 0; this._emit(ev, 'robot.telegraph');
      } else if (this.stateT > 4.0) {
        this.state = 'patrol'; this._emit(ev, 'robot.resume');
      }
    }

    // Mass: speed approaches its target instead of stepping. ~0.8 s to full charge.
    this.speed += (wantSpeed - this.speed) * Math.min(1, dt * 2.6);

    // --- movement ----------------------------------------------------------
    let travel = this.speed * dt;
    if (this.state === 'engage') {
      const fx = Math.sin(this.heading), fz = Math.cos(this.heading);
      let nx = this.pos.x + fx * travel, nz = this.pos.z + fz * travel;
      [nx, nz] = this.world.resolve(nx, nz, 0.55, this.pos.y, this.pos.y + 2.4);
      travel = Math.hypot(nx - this.pos.x, nz - this.pos.z);
      this.pos.x = nx; this.pos.z = nz;
    } else if (this.state === 'search') {
      const sx = this.lastSeen.x - this.pos.x, sz = this.lastSeen.z - this.pos.z;
      const sd = Math.hypot(sx, sz);
      if (sd > 0.6) {
        this._turnToward(Math.atan2(sx, sz), dt, 1.6);
        const fx = Math.sin(this.heading), fz = Math.cos(this.heading);
        let nx = this.pos.x + fx * travel, nz = this.pos.z + fz * travel;
        [nx, nz] = this.world.resolve(nx, nz, 0.55, this.pos.y, this.pos.y + 2.4);
        this.pos.x = nx; this.pos.z = nz;
      } else {
        travel = 0;
        // Sweep the head while parked at the last-seen point.
        headLook = null;
        this.head.rotation.y = Math.sin(this.stateT * 1.6) * 1.2;
      }
    } else {
      // Route follow.
      const R = this.route;
      const a = R[this.seg], b = R[(this.seg + 1) % R.length];
      const segLen = Math.hypot(b[0] - a[0], b[1] - a[1]);
      this.u += travel / Math.max(0.001, segLen);
      while (this.u >= 1) { this.u -= 1; this.seg = (this.seg + 1) % R.length; }
      const a2 = R[this.seg], b2 = R[(this.seg + 1) % R.length];
      this.pos.x = a2[0] + (b2[0] - a2[0]) * this.u;
      this.pos.z = a2[1] + (b2[1] - a2[1]) * this.u;
      this._turnToward(Math.atan2(b2[0] - a2[0], b2[1] - a2[1]), dt, 1.4);
      // World clearance on patrol (engage/search already resolve directly).
      const decay = Math.max(0, 1 - dt * 0.9);
      this.clearX *= decay; this.clearZ *= decay;
      const [rx, rz] = this.world.resolve(this.pos.x + this.clearX, this.pos.z + this.clearZ,
        0.9, this.pos.y + 0.1, this.pos.y + 2.3);
      this.clearX = rx - this.pos.x; this.clearZ = rz - this.pos.z;
      this.pos.x = rx; this.pos.z = rz;
    }
    this.gait.advance(travel);

    // --- head tracking -------------------------------------------------------
    if (headLook) {
      const hb = Math.atan2(headLook.x - this.pos.x, headLook.z - this.pos.z) - this.heading;
      const hy = Math.atan2(Math.sin(hb), Math.cos(hb));
      this.head.rotation.y += (Math.max(-1.3, Math.min(1.3, hy)) - this.head.rotation.y) * Math.min(1, dt * 6);
    } else if (this.state !== 'search') {
      this.head.rotation.y += (0 - this.head.rotation.y) * Math.min(1, dt * 3);
    }

    // --- lamp + beam by state ------------------------------------------------
    const red = this.state === 'telegraph' || this.state === 'engage' || this.state === 'confront';
    const pulse = this.state === 'telegraph' ? (0.5 + 0.5 * Math.sin(this.stateT * 22)) : 1;
    this.lampMat.emissive.setHex(red ? 0xff2222 : this.state === 'scan' || this.state === 'search' ? 0xffb020 : 0xffc37a);
    this.lampMat.emissiveIntensity = (red ? 3.4 : this.state === 'patrol' ? 2.2 : 2.8) * pulse;
    this.beam.color.setHex(red ? 0xff3b30 : 0xffc37a);
    this.beam.intensity = (red ? 62 : this.state === 'patrol' ? 22 : 40) * pulse;

    // --- carriage from the feet (same construction as the fauna) -------------
    const ga = (x, z) => this.world.groundAt(x, z, 0);
    for (const L of this.legs) this.gait.footTarget(L, this.pos, this.heading, this.speed, ga, L.target);
    let sumY = 0, fore = 0, aft = 0, left = 0, right = 0;
    for (const L of this.legs) {
      sumY += L.target.y;
      if (L.spec.hip[2] > 0) fore += L.target.y; else aft += L.target.y;
      if (L.spec.hip[0] > 0) right += L.target.y; else left += L.target.y;
    }
    const standY = sumY / this.legs.length + this.ride;
    this.pos.y += (standY - this.pos.y) * Math.min(1, dt * 5.0);
    this.root.position.copy(this.pos);
    this.root.rotation.y = this.heading;
    // WEIGHT. A critic: "the torso glides dead-level... it reads as a table being slid across
    // the floor." Three terms: a dip at each of the four footfalls, a roll AWAY from whichever
    // corner is unsupported (smoothed, so the mass appears to transfer rather than snap), and the
    // ground-derived pitch/roll from the feet.
    let swingX = 0;
    for (const L of this.legs) if (!L.contact) swingX += Math.sign(L.spec.hip[0]);
    this._lean = (this._lean || 0) + ((-swingX * 0.05) - (this._lean || 0)) * Math.min(1, dt * 3.2);
    this.body.rotation.x = Math.max(-0.12, Math.min(0.12, (aft - fore) * 0.35));
    this.body.rotation.z = Math.max(-0.14, Math.min(0.14, (right - left) * 0.3 + this._lean));
    this.body.position.y = -Math.abs(Math.sin(this.gait.t * Math.PI * 4)) * 0.055;

    this.root.updateMatrixWorld(true);
    this._inv.copy(this.body.matrixWorld).invert();
    for (const L of this.legs) L.solve(this._inv);
    return ev;
  }

  contactCount() { return this.legs.reduce((n, L) => n + (L.contact ? 1 : 0), 0); }

  /** Known state for the interaction probes: mid-route, calm, nobody seen. */
  reset() {
    this.state = 'patrol'; this.stateT = 0; this.speed = 0; this.losGone = 0;
    this.seg = 0; this.u = 0.5;
    this.pos.set(this.route[0][0], this.pos.y, (this.route[0][1] + this.route[1][1]) / 2);
    this.heading = Math.atan2(0, 1) * 0 + Math.atan2(this.route[1][0] - this.route[0][0], this.route[1][1] - this.route[0][1]);
  }
}
